import { useSuspenseQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { type Tenant, fetchTenant, isUnknownTenant } from '~/api/tenant';
import { forgetTenantOf, persistedTenantFor, rememberTenant } from '~/stores/tenant';

export const TENANT_STALE_TIME_MS = 5 * 60 * 1_000;

/** Um escritório lembrado conta como velho: a abertura sempre confere com o backend. */
const PERSISTED_TENANT_UPDATED_AT = 0;

async function resolveTenant(host: string): Promise<Tenant> {
  try {
    const tenant = await fetchTenant(host);
    rememberTenant(host, tenant);
    return tenant;
  } catch (error) {
    // Uma resposta definitiva de "sem escritório" invalida o lembrado; erro de rede não.
    if (isUnknownTenant(error)) {
      forgetTenantOf(host);
    }
    throw error;
  }
}

/**
 * O escritório do host. Sem nada lembrado, suspende até o backend responder e, se ele recusar,
 * lança para a `ErrorBoundary` do `TenantGate`. Com o escritório lembrado para o mesmo host, devolve
 * na hora e revalida em segundo plano; uma falha da revalidação (offline) mantém o lembrado, porque
 * o `useSuspenseQuery` só lança quando não há dado.
 */
export function useTenantQuery(host: string): Tenant {
  const { data } = useSuspenseQuery({
    queryKey: queryKeys.tenant.byHost(host),
    queryFn: () => resolveTenant(host),
    staleTime: TENANT_STALE_TIME_MS,
    initialData: () => persistedTenantFor(host),
    initialDataUpdatedAt: PERSISTED_TENANT_UPDATED_AT,
  });
  return data;
}
